import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { Overlay } from '@ipfabric/ui-components/Overlay/Overlay';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { Switch } from '@ipfabric/ui-components/Switch/Switch';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'components/Button/Button';
import { type FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';

type McpServerSettingsProps = {
  api: ReturnType<typeof useApi>;
  notifications: ReturnType<typeof useNotifications>;
  initialEnabled: boolean;
  isLoadingState: boolean;
};

const TermsOfUse: FC = () => {
  return (
    <div className='flex flex-col gap [&_h3]:font-semibold [&_h4]:font-semibold'>
      <div className='contents'>
        <h3>Local MCP Terms of Use</h3>
        <p>
          These terms apply only if Customer elects to enable the MCP Server
          functionality and govern Customer’s access to and use of such
          functionality as a supplement to the applicable license agreement. The
          MCP Server is an optional add-on feature. Customer is not required to
          enable it. The IP Fabric Network Assurance Platform operates and
          fulfils its core functionality without the MCP Server.{' '}
        </p>
        <p>
          Nothing in these MCP Server Terms modifies the core licensing rights
          or obligations under the applicable license agreement except as
          expressly stated herein. These MCP Server Terms supplement the
          applicable license agreement governing Customer’s use of the IP Fabric
          Product. In case of conflict, these terms prevail for the MCP Server
          and AI Integration only.
        </p>
        <h4>1. Definitions</h4>
        <p>
          For the purposes of these MCP Server Terms, the following definitions
          shall apply:
        </p>
        <ul>
          <li>
            <strong>“Agentic Use”</strong> means any use of the AI Integration
            in which an AI System takes automated actions, executes
            instructions, or makes decisions without real-time human review or
            approval of each individual action, including automated network
            queries, configuration reads, or multi-step workflows.
          </li>
          <li>
            <strong>“AI Act”</strong> means Regulation (EU) 2024/1689 of the
            European Parliament and of the Council of 13 June 2024 laying down
            harmonised rules on artificial intelligence, as amended or
            supplemented from time to time, together with any delegated or
            implementing acts, guidelines, or codes of practice issued
            thereunder.
          </li>
          <li>
            <strong>“AI Application”</strong> means the third-party interface or
            software (e.g., desktop client, agent framework or similar tool)
            through which a User submits prompts and receives responses.
          </li>
          <li>
            <strong>“AI Input”</strong> means any data, prompts, instructions,
            rulesets, queries, configurations, scripts, or other content
            submitted, authored, imported or used by Customer in connection with
            the AI Integration, including combinations of (i) User prompts, (ii)
            Customer-defined playbooks, workflows or configurations, and (iii)
            data retrieved through MCP Server calls.
          </li>
          <li>
            <strong>“AI Integration”</strong> means Customer's use of an MCP
            Client with the MCP Server in connection with an AI Application
            and/or AI System, including any use that (a) causes MCP Responses or
            Customer Data to be transmitted to an AI System or LLM Provider, or
            (b) results in AI Outputs.
          </li>
          <li>
            <strong>“AI Output”</strong> means any response, instruction,
            recommendation, script, configuration, or other content generated by
            an AI System. For the avoidance of doubt, AI Outputs are not
            generated, authored, or controlled by IP Fabric.
          </li>
          <li>
            <strong>“AI Systems”</strong> mean third-party artificial
            intelligence systems, large language models, agents, and related
            tools, including any LLM Provider.
          </li>
          <li>
            <strong>“Customer”</strong> means the legal entity that has entered
            into the applicable license agreement with IP Fabric and has elected
            to enable the MCP Server functionality in accordance with these MCP
            Server Terms. Where these MCP Server Terms refer to Customer, they
            include Customer's affiliates, employees, contractors, and agents
            acting on Customer's behalf, to the extent such persons are
            authorised to access the IP Fabric Product under the applicable
            license agreement.
          </li>
          <li>
            <strong>“Customer License Agreement”</strong> means the license
            agreement entered into between IP Fabric and the Customer regarding
            the provision of the IP Fabric Automated Infrastructure Assurance
            Platform Software
            <strong>.</strong>
          </li>
          <li>
            <strong>“GDPR”</strong> means Regulation (EU) 2016/679 of the
            European Parliament and of the Council of 27 April 2016 on the
            protection of natural persons with regard to the processing of
            personal data and on the free movement of such data, and repealing
            Directive 95/46/EC.
          </li>
          <li>
            <strong>“LLM Provider”</strong> means the third-party provider of a
            large language model that processes inputs and generates outputs.
          </li>
          <li>
            <strong>“MCP Client”</strong> means the third-party component
            residing within the AI Application that communicates with the MCP
            Server and executes instructions.
          </li>
          <li>
            <strong>“MCP Responses”</strong> means data, results or other
            outputs returned by the MCP Server in response to calls from an MCP
            Client.
          </li>
          <li>
            <strong>“MCP Server”</strong> means the IP Fabric component that
            exposes tools, resources, or prompts for use by an AI Application.
          </li>
          <li>
            <strong>“User”</strong> means an individual interacting with an AI
            Application authorised by the Customer.
          </li>
        </ul>
        <h4>2. Integration with Third-Party AI Systems.</h4>
        <p>
          2.1. The MCP Server enables interoperability between IP Fabric
          platform and AI Systems. These AI Systems are not provided, operated,
          or controlled by IP Fabric..
        </p>
        <p>
          2.2. Customer expressly acknowledges and accepts all risks associated
          with integrating third-party AI Systems, AI Applications, and LLM
          Providers with the MCP Server, including but not limited to risks
          relating to data security, prompt injection, model hallucination,
          unintended automated actions, regulatory non-compliance, and the
          inherent unpredictability of AI Systems.
        </p>
        <p>
          2.3. IP Fabric makes no representations or warranties that the MCP
          Server will be compatible with any particular AI Application, MCP
          Client, AI System, or LLM Provider. IP Fabric does not test, certify,
          or endorse any third-party AI System for use with the MCP Server.
        </p>
        <p>
          2.4. Third-party AI Systems, AI Applications, and LLM Providers may
          change their interfaces, APIs, models, or terms of service at any time
          without notice to IP Fabric. IP Fabric shall not be liable for any
          disruption, failure, or adverse outcome arising from such third-party
          changes.
        </p>
        <h4>3. AI-Generated Outputs.</h4>
        <p>
          3.1. IP Fabric does not generate, control, monitor, or validate AI
          Outputs. AI Outputs are generated by the applicable AI System and/or
          LLM Provider in response to:
        </p>
        <p>3.1.1. AI Inputs submitted via the AI Application, and</p>
        <p>
          3.1.2. MCP Responses coming from the MCP server, containing network
          data stored in IP Fabric platform, as configured by Customer.
        </p>
        <p>
          3.2. AI Outputs may be incomplete, inaccurate, or inappropriate for
          Customer’s intended use. Customer is solely responsible for reviewing,
          validating, and approving all AI Outputs prior to reliance or
          implementation. The quality, completeness, accuracy, and relevance of
          AI Outputs depend directly on the completeness, accuracy, and
          integrity of the data made available to the AI System via the MCP
          Server. IP Fabric makes no representation that AI Outputs will be
          complete, accurate, or fit for purpose where Customer has restricted,
          filtered, modified, or otherwise limited the scope of data accessible
          to the MCP Server. IP Fabric shall have no liability for any AI Output
          that is incomplete, inaccurate, or misleading as a result of
          incomplete or modified data inputs, Customer's configuration choices,
          or any limitations in the data accessible to the MCP Server.
        </p>
        <p>
          3.3. IP Fabric shall have no liability for any direct or indirect
          damages, losses, or adverse consequences arising from Customer's
          reliance on, implementation of, or failure to review any AI Output.
        </p>
        <p>
          3.4. Customer acknowledges that AI Systems may be susceptible to
          prompt injection attacks and other adversarial manipulation techniques
          that could cause AI Systems to generate harmful, misleading, or
          unintended outputs. IP Fabric is not responsible for detecting,
          preventing, or remediating any such manipulation and any consequences
          thereof.
        </p>
        <h4>4. Disclaimer of Warranties.</h4>
        <p>
          4.1. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE MCP
          SERVER, ANY AI INTEGRATION, AND ALL RELATED FUNCTIONALITY, DATA, AND
          OUTPUTS (INCLUDING ANY MCP RESPONSES AND AI OUTPUTS) ARE PROVIDED ON
          AN “AS IS” AND “AS AVAILABLE” BASIS.
        </p>
        <p>
          4.2. IP FABRIC MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND,
          WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING, WITHOUT
          LIMITATION, ANY WARRANTIES OR GUARANTEES THAT:
        </p>
        <p>
          4.2.1 THE MCP SERVER, AI INTEGRATION, OR ANY RELATED FUNCTIONALITY
          WILL BE CONTINUOUSLY AVAILABLE, ACCESSIBLE, OR OPERATE WITHOUT
          INTERRUPTION OR DELAY;
        </p>
        <p>
          4.2.2. THE MCP SERVER, MCP RESPONSES, AI OUTPUTS, OR ANY DATA OR
          RESULTS WILL BE ACCURATE, COMPLETE, RELIABLE, CURRENT, ERROR-FREE, OR
          FREE FROM DEFECTS, BUGS, OR HARMFUL COMPONENTS;
        </p>
        <p>
          4.2.3. ANY ERRORS, DEFECTS, OR FAILURES WILL BE DETECTED, CORRECTED,
          OR REMEDIED;
        </p>
        <p>
          4.2.4. THE MCP SERVER OR ANY AI INTEGRATION WILL MEET CUSTOMER’S
          REQUIREMENTS, EXPECTATIONS, OR INTENDED PURPOSES, OR WILL PRODUCE ANY
          PARTICULAR RESULT OR OUTCOME;
        </p>
        <p>
          4.2.5. ANY AI OUTPUTS, RECOMMENDATIONS, SCRIPTS, CONFIGURATIONS, OR
          OTHER RESULTS GENERATED THROUGH THE AI INTEGRATION WILL BE CORRECT,
          SAFE, LAWFUL, OR SUITABLE FOR IMPLEMENTATION OR RELIANCE;
        </p>
        <p>
          4.2.6. THE MCP SERVER OR ANY AI INTEGRATION WILL BE COMPATIBLE WITH
          ANY PARTICULAR AI SYSTEM, AI APPLICATION, MCP CLIENT, OR THIRD-PARTY
          SERVICE;
        </p>
        <p>
          4.2.7. THE MCP SERVER OR ANY AI INTEGRATION WILL BE SECURE, OR THAT
          ANY USE THEREOF WILL BE FREE FROM UNAUTHORISED ACCESS, DATA LOSS, DATA
          CORRUPTION, OR OTHER SECURITY INCIDENTS;
        </p>
        <p>
          4.2.8. ANY DATA PROCESSED, TRANSMITTED, OR MADE AVAILABLE THROUGH THE
          MCP SERVER OR AI INTEGRATION WILL REMAIN CONFIDENTIAL, SECURE, OR
          UNAFFECTED BY THIRD-PARTY SYSTEMS OR PROVIDERS.
        </p>
        <p>
          4.3. WITHOUT LIMITING THE FOREGOING, CUSTOMER ACKNOWLEDGES THAT AI
          SYSTEMS AND AI OUTPUTS ARE INHERENTLY PROBABILISTIC, MAY PRODUCE
          INCORRECT, INCOMPLETE, OR MISLEADING RESULTS (INCLUDING SO-CALLED
          “HALLUCINATIONS”), AND ARE NOT A SUBSTITUTE FOR HUMAN REVIEW,
          PROFESSIONAL JUDGMENT, OR INDEPENDENT VERIFICATION.
        </p>
        <p>
          4.4. ALL IMPLIED WARRANTIES, INCLUDING, WITHOUT LIMITATION, WARRANTIES
          OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
          NON-INFRINGEMENT, ARE HEREBY EXPRESSLY DISCLAIMED.
        </p>
        <h4>5. Limitation of Liability.</h4>
        <p>
          5.1. To the maximum extent permitted by applicable law, IP Fabric
          shall not be responsible or liable for any loss, damage, claim, or
          regulatory exposure, or third-party claim, whether direct or indirect,
          arising out of or related to:
        </p>
        <p>
          5.1.1. Customer’s use of the MCP Server with any AI Application, MCP
          Client, AI System, or LLM Provider;
        </p>
        <p>5.1.2. reliance on AI Outputs;</p>
        <p>5.1.3. actions taken or not taken based on AI Outputs;</p>
        <p>5.1.4. third-party AI provider conduct or failures;</p>
        <p>
          5.1.5. the content, structure, legality, or transmission of AI Inputs.
        </p>
        <p>
          5.2. In no event shall IP Fabric be liable for any indirect,
          incidental, special, exemplary, consequential, or punitive damages
          arising out of or related to the MCP Server or any AI Integration,
          including loss of data, loss of profits, loss of revenue, business
          interruption, or reputational damage, even if IP Fabric has been
          advised of the possibility of such damages.
        </p>
        <p>
          5.3. IP Fabric shall not be liable for any regulatory penalties,
          fines, enforcement actions, or compliance failures arising from
          Customer's use of the MCP Server or any AI Integration, including but
          not limited to violations of data protection laws, financial
          regulations, cybersecurity requirements, or sector-specific AI
          regulations.
        </p>
        <h4>6. Customer Responsibility.</h4>
        <p>6.1. Customer is responsible for:</p>
        <p>6.1.1. selecting and configuring AI Systems and AI Applications;</p>
        <p>
          6.1.2. implementing appropriate internal safeguards and human
          oversight over Users and AI Integration; most importantly, the MCP
          server should not be used with permissions allowing write access to a
          production IP Fabric platform without human oversight –
          experimentation in less sensitive “staging” environments is
          recommended;
        </p>
        <p>
          6.1.3. ensuring compliance with applicable laws and internal policies
          when using AI Integration. Customer is solely responsible for ensuring
          that its use of the MCP Server and any AI Integration complies with
          all applicable artificial intelligence laws and regulations, including
          but not limited to the AI Act and any applicable national implementing
          legislation, sector-specific AI requirements, and applicable data
          protection laws including the GDPR;
        </p>
        <p>
          6.1.4. the content, structure, legality, and appropriateness of AI
          Inputs and any resulting AI Integration;
        </p>
        <p>
          6.1.5. ensuring AI Inputs do not contain unlawful, infringing,
          confidential, regulated, or sensitive data unless appropriate
          safeguards are implemented;
        </p>
        <p>
          6.1.6. ensuring that any Customer Data transmitted to AI Systems or
          LLM Providers via an AI Application or MCP Client is authorized for
          such transmission.
        </p>
        <p>
          6.1.7. implementing appropriate safeguards to ensure that automated
          actions are limited in scope, reversible where possible, and subject
          to appropriate human oversight. Customer acknowledges that Agentic Use
          introduces elevated risk of unintended consequences and agrees that IP
          Fabric shall bear no liability for any actions taken by an AI System
          acting autonomously in connection with the MCP Server.
        </p>
        <p>
          6.1.8. ensuring that all Users are informed about the nature and
          limitations of AI Integration, including the fact that outputs are
          generated by third-party AI Systems and not by IP Fabric, and that
          such outputs require human review prior to implementation.
        </p>
        <p>6.2. Scope of Data Access</p>
        <p>
          6.2.1. The MCP Server is deployed as a separate component to IP Fabric
          and does not have access to Customer data by default. In order to
          function, the MCP Server must be granted access permissions within the
          IP Fabric environment. To the extent such access is enabled by
          Customer, the MCP Server may access Customer’s network data,
          configurations, metadata, documentation, and related information (“
          <strong>Customer Data</strong>”).
        </p>
        <p>
          6.2.2. Where Customer connects an AI System to the MCP Server and
          enables such AI Integration, Customer Data accessible by the MCP
          Server may be retrieved, processed, analyzed, stored, or transmitted
          to the connected AI System in accordance with the permissions and
          configuration established by Customer. MCP Server returns MCP
          Responses to the MCP Client/AI Application, and those components
          (under Customer control) may forward data to the AI System/LLM
          Provider.
        </p>
        <p>
          6.2.3. Customer acknowledges and agrees that once Customer Data is
          accessed or transmitted to an AI System, IP Fabric does not control
          and assumes no responsibility for how such AI System processes,
          stores, retains, transfers, secures, discloses, or otherwise uses that
          data.
        </p>
        <p>
          6.2.4. The security, confidentiality, and regulatory compliance of
          Customer Data, once shared with an AI System, depend on the safeguards
          and practices of the selected AI provider.
        </p>
        <p>
          6.2.5. For environments involving highly sensitive, regulated, or
          strategically critical data, Customer should carefully evaluate the
          suitability of any external AI System prior to enabling connectivity.
          As a best practice, Customers requiring enhanced data control, data
          residency assurance, or isolation from third-party environments may
          consider private, dedicated, or self-hosted AI deployments.
        </p>
        <p>
          6.2.6. For the avoidance of doubt, the current version of the MCP
          Server does not communicate directly with any LLM Provider. All
          interactions with AI Systems occur via the AI Application and MCP
          Client under Customer’s control. This is subject to change in future
          MCP server releases. In case the MCP server does start communicating
          with an LLM Provider in future releases, this feature will be opt-in,
          disabled by default, and communicated along with the release at the
          latest.
        </p>
        <p>
          6.3. Prohibited Uses. Customer shall not use the MCP Server or any AI
          Integration:
        </p>
        <p>
          6.3.1. in a manner that violates any applicable law or regulation;
        </p>
        <p>
          6.3.2. to make automated changes to production network infrastructure
          without human oversight;
        </p>
        <p>
          6.3.3. to process personal data in violation of applicable data
          protection laws;
        </p>
        <p>
          6.3.4. in connection with any AI System or AI Application that has not
          been appropriately evaluated and authorised by Customer for such use;
        </p>
        <p>
          6.3.5. to circumvent any technical restrictions, access controls, or
          usage limitations imposed by IP Fabric in connection with the MCP
          Server;
        </p>
        <p>
          6.3.6. in a manner that could damage, disable, overburden, or impair
          the IP Fabric platform or infrastructure.
        </p>
        <h4>7. Third-Party Fees.</h4>
        <p>
          7.1. IP Fabric is not responsible for any fees, token usage charges,
          API costs, or other expenses imposed by third-party AI providers.
        </p>
        <p>
          7.2. Customer is solely responsible for reviewing and complying with
          the terms of service, privacy policies, and any other applicable terms
          of any third-party AI System, AI Application, MCP Client, or LLM
          Provider used in connection with the AI Integration.
        </p>
        <p>
          7.3. IP Fabric is not a party to any agreement between Customer and
          any third-party AI System or LLM Provider. Any dispute between
          Customer and a third-party AI System or LLM Provider is solely between
          Customer and such third party.
        </p>
        <h4>8. Intellectual Property.</h4>
        <p>
          8.1. The MCP Server, including all underlying software, interfaces,
          APIs, tooling, documentation, and related technology as well as all
          MCP Responses provided in connection therewith, is and shall remain
          the exclusive intellectual property of IP Fabric. Customer's use of
          the MCP Server or MCP Responses does not transfer, assign, or grant
          Customer any ownership rights in the MCP Server or any IP Fabric
          technology or MCP Responses. Customer shall not copy, decompile,
          disassemble, reverse engineer, or otherwise attempt to extract or
          derive the source code or any methods, algorithms, or procedures from
          the MCP Server or MCP Responses, or modify, adapt, translate, or
          create derivative works based upon the MCP Server or MCP Responses,
          except as expressly authorised by the mandatory provisions of
          applicable law.
        </p>
        <p>
          8.2. Subject to Customer's compliance with these MCP Server Terms and
          the Customer License Agreement, IP Fabric hereby grants to Customer a
          non-exclusive, non-transferable, non-sublicensable, revocable licence,
          for the term of the Customer License Agreement, to install, configure,
          and use the MCP Server and MCP Responses solely for Customer's
          internal business operations and in accordance with these MCP Server
          Terms.
        </p>
        <p>
          8.3. MCP Responses are derived from Customer Data and are returned to
          the MCP Client for Customer's own use. IP Fabric retains all rights in
          the format, structure, logic, and tooling used to generate MCP
          Responses.
        </p>
        <p>
          8.4. AI Output Ownership. IP Fabric makes no claim to any AI Output.
          Customer acknowledges that the ownership, copyright status, and
          usability of AI Outputs are determined solely by the applicable AI
          System's and LLM Provider's terms of service and applicable law, and
          that IP Fabric has no role in such determination.
        </p>
        <p>
          8.5. If Customer provides IP Fabric with any feedback, suggestions, or
          recommendations regarding the MCP Server (including the AI Integration
          and AI Outputs), it shall ensure that it has sufficient authorization
          to grant IP Fabric a perpetual (for the duration of economic rights in
          copyright), irrevocable, royalty-free, worldwide, sublicensable
          licence to use, incorporate, modify, and commercialise such feedback
          without any obligation or payment of any kind to Customer or any third
          party.
        </p>
        <h4>9. Indemnification.</h4>
        <p>
          9.1. Customer shall indemnify, defend, and hold harmless IP Fabric and
          its affiliates, directors, officers, employees, and agents from and
          against any and all third-party claims, demands, actions, losses,
          damages, liabilities, costs, and expenses (including reasonable legal
          fees) arising out of or relating to:
        </p>
        <p>9.1.1. Customer's breach of these MCP Server Terms;</p>
        <p>
          9.1.2. Customer's violation of any applicable law or regulation in
          connection with the MCP Server or AI Integration;
        </p>
        <p>
          9.1.3. any claim that Customer Data transmitted to an AI System
          infringes the intellectual property rights or other rights of any
          third party;
        </p>
        <p>
          9.1.4. any claim arising from or relating to AI Outputs, including any
          claim that an AI Output is inaccurate, misleading, defamatory, or
          otherwise harmful;
        </p>
        <p>
          9.1.5. any claim arising from or relating to Agentic Use, including
          any automated action taken by an AI System in connection with the MCP
          Server without adequate human oversight;
        </p>
        <p>
          9.1.6. Customer's use of the MCP Server or AI Integration in breach of
          Section 6.3 (Prohibited Uses);
        </p>
        <p>
          9.1.7. all claims arising from or alleging fraud, intentional
          misconduct, criminal acts, or gross negligence committed by Customer
          or its Users in connection with the MCP Server or any AI Integration.
        </p>
        <h4>10. Data Protection.</h4>
        <p>
          10.1. Customer acknowledges that the MCP Server may transmit data that
          includes personal data within the meaning of applicable data
          protection law, including the GDPR or equivalent legislation.
        </p>
        <p>
          10.2. Customer is the data controller in respect of any personal data
          processed in connection with the AI Integration. IP Fabric's role is
          limited to providing the MCP Server as a technical conduit; IP Fabric
          does not access, retrieve, record, organise, alter, store or otherwise
          process data transmitted by Customer to third-party AI Systems via the
          AI Integration and is not a data processor in respect of such data
          including any personal data.
        </p>
        <p>
          10.3. Customer is solely responsible for ensuring that any personal
          data transmitted to third-party AI Systems via the AI Integration is
          done so in compliance with applicable data protection laws, including,
          but not limited to, obtaining all necessary consents, transparently
          informing data subjects and implementing appropriate technical and
          organisational measures and safeguards (e.g., standard contractual
          clauses, adequacy decisions, or other transfer mechanisms where
          applicable).
        </p>
        <h4>11. Monitoring.</h4>
        <p>
          11.1. IP Fabric reserves the right to monitor aggregate usage patterns
          of the MCP Server (in anonymised or aggregated form) for the purpose
          of improving the MCP Server, identifying security issues, and ensuring
          compliance with these Terms. Upon request by IP Fabric but not more
          than once per calendar year, Customer shall conduct a self-audit of
          its use of the MCP Server and AI Integration and, within ten (10)
          business days after receipt of such request, submit a written
          statement to IP Fabric verifying that it is in compliance with these
          MCP Server Terms (including sufficient proof thereof).
        </p>
        <h4>12. MCP Server Remedies and Cessation of Use</h4>
        <p>
          12.1. Customer acknowledges that the MCP Server is deployed and
          operates locally within Customer's own environment and that IP Fabric
          does not have remote access to, or technical control over, the MCP
          Server instance or any data processed through it. IP Fabric may, at
          its sole discretion and without prejudice to any other rights or
          remedies available to it:
        </p>
        <p>
          12.1.1. require Customer to immediately cease all use of the MCP
          Server and any AI Integration, and Customer shall comply with such
          requirement without undue delay;
        </p>
        <p>
          12.1.2. suspend or withdraw any support, maintenance, updates, or new
          releases of the MCP Server to Customer;
        </p>
        <p>
          12.1.3. terminate Customer's licence to use the MCP Server, and in the
          case of a repeated (i.e. more than two occurrences) material breach
          that is not remedied without undue delay, IP Fabric reserves the right
          to exercise its right to terminate the Customer License Agreement in
          accordance therewith.
        </p>
        <p>
          12.2. The remedies set out in Section 12 may be exercised where IP
          Fabric reasonably determines that: (a) Customer's use of the MCP
          Server poses an immediate or serious risk to the security, integrity,
          or availability of the IP Fabric platform or infrastructure; (b)
          Customer is using the MCP Server in a manner that could expose IP
          Fabric to legal liability or regulatory action; or (c) Customer has
          materially breached these MCP Server Terms or the applicable Customer
          License Agreement. IP Fabric shall notify Customer of the exercise of
          any such remedy as soon as reasonably practicable. Upon receipt of a
          notice requiring cessation of use under Section 12.1.1, Customer shall
          promptly disable the MCP Server and provide written confirmation to IP
          Fabric that all use of the MCP Server and any AI Integration has
          ceased.
        </p>
        <p>
          12.3. Sections 3 (AI-Generated Outputs), 4 (Disclaimer of Warranties),
          5 (Limitation of Liability), 8 (Intellectual Property), 9
          (Indemnification), 10 (Data Protection), and 13 (Miscellaneous),
          together with any other provisions of these MCP Server Terms that by
          their nature should survive, shall survive the termination or
          expiration of these MCP Server Terms for any reason.
        </p>
        <h4>13. Miscellaneous.</h4>
        <p>
          13.1. These MCP Server Terms shall be governed by and construed in
          accordance with the law applicable under the main Customer License
          Agreement between IP Fabric and the Customer.
        </p>
        <p>
          13.2. These MCP Server Terms constitute the entire agreement between
          the parties with respect to the subject matter hereof and supersede
          any prior or contemporaneous representations or agreements relating to
          the MCP Server.
        </p>
        <p>
          13.3. No waiver of any provision of these MCP Server Terms shall be
          effective unless in writing and signed by an authorised representative
          of IP Fabric.
        </p>
        <p>
          13.4. If any provision of these MCP Server Terms is adjudicated to be
          unenforceable, such provision shall be changed and interpreted to
          accomplish the objectives of such provision to the greatest extent
          possible under applicable law and the remaining provisions shall
          continue in full force and effect.
        </p>
        <p>
          13.5. IP Fabric reserves the right, in its sole and absolute
          discretion, to revise, update, change, or modify these MCP Server
          Terms for security, legal, best practice, or regulatory reasons. IP
          Fabric shall inform Customer of any such changes. Continued use of the
          MCP Server following notification of any such changes shall constitute
          Customer's acceptance of the revised MCP Server Terms.
        </p>
        <p>
          13. 6. IP Fabric reserves the right to revise, update, or modify these
          MCP Server Terms for security, legal, best practice, or regulatory
          reasons. IP Fabric shall notify the Customer of any such changes at
          least thirty (30) days prior to their effective date. If Customer does
          not agree with the revised MCP Server Terms, Customer's sole remedy
          shall be to cease use of the MCP Server prior to the effective date of
          the changes. Continued use of the MCP Server following the effective
          date of any such changes shall constitute Customer's acceptance of the
          revised MCP Server Terms.
        </p>
      </div>
    </div>
  );
};

type TermsModalMode = 'accept' | 'review';

type TermsModalProps = {
  isAccepting: boolean;
  mode: TermsModalMode;
  onAccept: () => void;
  onClose: () => void;
};

const TermsModal: FC<TermsModalProps> = ({
  isAccepting,
  mode,
  onAccept,
  onClose,
}) => {
  const termsBodyRef = useRef<HTMLDivElement>(null);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);

  const evaluateScrollPosition = useStableCallback(() => {
    const termsBody = termsBodyRef.current;
    if (
      termsBody &&
      termsBody.scrollTop + termsBody.clientHeight >= termsBody.scrollHeight - 4
    ) {
      setHasReachedBottom(true);
    }
  });

  useLayoutEffect(() => {
    evaluateScrollPosition();
  }, []);

  useEffect(() => {
    const termsBody = termsBodyRef.current;
    const resizeObserver = new ResizeObserver(evaluateScrollPosition);

    window.addEventListener('resize', evaluateScrollPosition);
    if (termsBody) {
      resizeObserver.observe(termsBody);
    }

    return () => {
      window.removeEventListener('resize', evaluateScrollPosition);
      resizeObserver.disconnect();
    };
  }, []);

  const acceptDisabled = !hasReachedBottom || isAccepting;
  const acceptReasonId = 'mcp-terms-accept-reason';

  return (
    <Overlay alignment='center' onClose={onClose}>
      {/*
        `static` overrides the UA stylesheet's `position: absolute` on `dialog[open]`,
        which would take the dialog out of flow and defeat the Overlay's flex centering.
      */}
      <dialog
        aria-labelledby='mcp-terms-title'
        aria-modal='true'
        className='bg-default shadow-3xl static m-0 flex max-h-[80vh] w-[620px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg border-0 p-0 text-default'
        open
      >
        <div className='border-b-default flex shrink-0 items-center justify-between border-b p'>
          <h3
            className='text-level-3 m-0 font-semibold text-default'
            id='mcp-terms-title'
          >
            Terms &amp; Conditions
          </h3>
          <Button
            aria-label='Close Terms & Conditions'
            className='-m-r'
            icon='fa-close'
            onClick={onClose}
            type='button'
            variant='tertiary'
          />
        </div>

        <div
          className='cross-browser-scrollbar min-h-0 grow overflow-auto px py'
          onScroll={evaluateScrollPosition}
          ref={termsBodyRef}
          tabIndex={0}
        >
          <TermsOfUse />
        </div>

        <div className='border-t-default shrink-0 border-t p'>
          {mode === 'accept' ? (
            <div className='flex items-end justify-between gap'>
              <div className='flex flex-col gap-2xs'>
                <Button
                  aria-describedby={
                    !hasReachedBottom ? acceptReasonId : undefined
                  }
                  aria-disabled={acceptDisabled}
                  disabled={acceptDisabled}
                  loading={isAccepting}
                  onClick={onAccept}
                  type='button'
                  variant='primary'
                >
                  Accept &amp; Enable MCP server
                </Button>
                {!hasReachedBottom && (
                  <p className='m-0 text-xs text-secondary' id={acceptReasonId}>
                    Read the terms to the end to enable this action.
                  </p>
                )}
              </div>
              <Button onClick={onClose} type='button' variant='secondary'>
                Cancel
              </Button>
            </div>
          ) : (
            <div className='flex justify-end'>
              <Button onClick={onClose} type='button' variant='secondary'>
                Close
              </Button>
            </div>
          )}
        </div>
      </dialog>
    </Overlay>
  );
};

const McpServerSettings: FC<McpServerSettingsProps> = ({
  api,
  notifications,
  initialEnabled,
  isLoadingState,
}) => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(initialEnabled);
  const [isEnabled, setIsEnabled] = useState(initialEnabled);
  const [isUpdating, setIsUpdating] = useState(false);
  const [termsModalMode, setTermsModalMode] = useState<TermsModalMode | null>(
    null,
  );

  const updateEnabled = useStableCallback(
    async (enable: boolean): Promise<boolean> => {
      setIsUpdating(true);
      try {
        await api.request({
          url: '/settings/mcp-server',
          method: 'POST',
          data: { enable },
        });

        setIsEnabled(enable);
        notifications.showNotification({
          type: 'success',
          message: `MCP server successfully ${enable ? 'enabled' : 'disabled'}.`,
        });
        return true;
      } catch {
        notifications.showNotification({
          type: 'danger',
          message: `Failed to ${enable ? 'enable' : 'disable'} MCP server.`,
        });
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
  );

  const handleToggle = useStableCallback((enable: boolean) => {
    if (enable && !hasAcceptedTerms) {
      setTermsModalMode('accept');
      return;
    }

    void updateEnabled(enable);
  });

  const handleAccept = useStableCallback(async (): Promise<void> => {
    if (await updateEnabled(true)) {
      setHasAcceptedTerms(true);
      setTermsModalMode(null);
    }
  });

  const closeTermsModal = useStableCallback(() => {
    setTermsModalMode(null);
  });

  const isControlDisabled = isLoadingState || isUpdating;

  return (
    <div className='flex flex-col items-center'>
      <PageHeader elevated label='MCP Server' width='content-default' />
      <div className='w-full max-w-content-default flex flex-col px mt mb gap'>
        <Card className='flex flex-col gap p'>
          <div className='flex items-center gap-2'>
            <h3 className='text-level-4 m-0 font-semibold'>Local MCP server</h3>
            <Badge color={isEnabled ? 'success' : 'neutral'}>
              {isEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>

          <div className='flex items-center gap-2'>
            <Switch
              dataTestId='mcp-server-enabled'
              disabled={isControlDisabled}
              id='mcp-server-enabled'
              onChange={handleToggle}
              value={isEnabled}
            />
            <label htmlFor='mcp-server-enabled'>
              {isEnabled
                ? 'MCP server is running'
                : 'MCP server is not running'}
            </label>
          </div>

          <p>
            The MCP server is a separate component, disabled by default and
            governed by its own MCP Server Terms &amp; Conditions. It enables AI
            Systems, as defined in the MCP Server Terms &amp; Conditions, to
            access network data collected by IP Fabric. Before enabling and
            using it, read through the MCP Server Terms &amp; Conditions to
            understand the architecture and related risks.
          </p>

          <div className='flex flex-row gap'>
            <button
              className='text-link-legacy cursor-pointer border-0 bg-transparent p-0'
              onClick={() => setTermsModalMode('review')}
              type='button'
            >
              Terms &amp; Conditions
            </button>
            <a
              className='text-link-legacy'
              href='https://docs.ipfabric.io/latest/IP_Fabric_Settings/integration/mcp/'
              rel='noreferrer'
              target='_blank'
            >
              MCP documentation
            </a>
          </div>
        </Card>
      </div>

      {termsModalMode && (
        <TermsModal
          isAccepting={isUpdating}
          mode={termsModalMode}
          onAccept={() => {
            void handleAccept();
          }}
          onClose={closeTermsModal}
        />
      )}
    </div>
  );
};

export const McpServer: FC = () => {
  const api = useApi();
  const notifications = useNotifications();
  const mcpStateQuery = useQuery(
    api.query({
      url: '/settings/mcp-server',
      method: 'GET',
    }),
  );
  const backendEnabled = mcpStateQuery.isSuccess && mcpStateQuery.data?.enabled;
  const formKey = mcpStateQuery.isSuccess
    ? `mcp-server-loaded-${backendEnabled}`
    : 'mcp-server-loading';

  return (
    <McpServerSettings
      key={formKey}
      api={api}
      notifications={notifications}
      initialEnabled={backendEnabled === true}
      isLoadingState={mcpStateQuery.isPending}
    />
  );
};
